/**
 * Pickup locations — the places a processor lets customers collect finished
 * product: the plant, a weekly delivery run into a city, a farmers' market
 * stall. Stored one-per-document in the `PickupLocations` collection, owned by
 * a vendor.
 *
 * These caps are shared so the settings editor can warn *before* a save and the
 * `pickupLocationUpsert` resolver can reject *at* the save with matching
 * numbers. The resolver is the authority; the client copy is advisory.
 */

/**
 * Cap on a location's free-text `description`.
 *
 * Generous on purpose: in the first pass this single field carries the entire
 * schedule — days, windows, parking, what to bring, how the customer is
 * contacted — because structured recurrence is deliberately not modeled yet.
 */
export const PICKUP_LOCATION_DESCRIPTION_MAX = 2000;

/**
 * Cap on ACTIVE locations per vendor. Archiving frees a slot. This exists to
 * catch runaway or automated creation, not to ration real locations — no
 * processor is expected to approach it.
 */
export const MAX_PICKUP_LOCATIONS_PER_VENDOR = 25;

/**
 * The postal parts a pickup location may carry. Every one optional (SUP-300) —
 * a stall or a parking-lot meetup has no street address worth storing.
 */
export interface PickupLocationAddressParts {
  address_1?: string | null;
  address_2?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
}

/**
 * US states, districts and territories the platform operates in, keyed by full
 * name so a spelled-out value can be NORMALIZED rather than rejected.
 *
 * Rejecting was the first cut, and it regressed a step that already worked:
 * the vendor's own facility `state` is free-text and has never been
 * normalized, so a processor whose facility says "Colorado" clicked "Use my
 * facility address", got the seeded value marked invalid, and could no longer
 * save the row at all — including rows that had saved fine for months, since
 * the editor round-trips the whole address block on every write. Accepting
 * both spellings and storing the code also gives the geocoder a more
 * predictable string (PRO-689 review).
 *
 * Keys are punctuation-free and single-spaced; `normalizeUsStateCode` folds
 * the input the same way before looking it up.
 */
const US_STATE_CODES_BY_NAME = new Map<string, string>([
  ['ALABAMA', 'AL'],
  ['ALASKA', 'AK'],
  ['ARIZONA', 'AZ'],
  ['ARKANSAS', 'AR'],
  ['CALIFORNIA', 'CA'],
  ['COLORADO', 'CO'],
  ['CONNECTICUT', 'CT'],
  ['DELAWARE', 'DE'],
  ['FLORIDA', 'FL'],
  ['GEORGIA', 'GA'],
  ['HAWAII', 'HI'],
  ['IDAHO', 'ID'],
  ['ILLINOIS', 'IL'],
  ['INDIANA', 'IN'],
  ['IOWA', 'IA'],
  ['KANSAS', 'KS'],
  ['KENTUCKY', 'KY'],
  ['LOUISIANA', 'LA'],
  ['MAINE', 'ME'],
  ['MARYLAND', 'MD'],
  ['MASSACHUSETTS', 'MA'],
  ['MICHIGAN', 'MI'],
  ['MINNESOTA', 'MN'],
  ['MISSISSIPPI', 'MS'],
  ['MISSOURI', 'MO'],
  ['MONTANA', 'MT'],
  ['NEBRASKA', 'NE'],
  ['NEVADA', 'NV'],
  ['NEW HAMPSHIRE', 'NH'],
  ['NEW JERSEY', 'NJ'],
  ['NEW MEXICO', 'NM'],
  ['NEW YORK', 'NY'],
  ['NORTH CAROLINA', 'NC'],
  ['NORTH DAKOTA', 'ND'],
  ['OHIO', 'OH'],
  ['OKLAHOMA', 'OK'],
  ['OREGON', 'OR'],
  ['PENNSYLVANIA', 'PA'],
  ['RHODE ISLAND', 'RI'],
  ['SOUTH CAROLINA', 'SC'],
  ['SOUTH DAKOTA', 'SD'],
  ['TENNESSEE', 'TN'],
  ['TEXAS', 'TX'],
  ['UTAH', 'UT'],
  ['VERMONT', 'VT'],
  ['VIRGINIA', 'VA'],
  ['WASHINGTON', 'WA'],
  ['WEST VIRGINIA', 'WV'],
  ['WISCONSIN', 'WI'],
  ['WYOMING', 'WY'],
  ['DISTRICT OF COLUMBIA', 'DC'],
  ['WASHINGTON DC', 'DC'],
  ['AMERICAN SAMOA', 'AS'],
  ['GUAM', 'GU'],
  ['NORTHERN MARIANA ISLANDS', 'MP'],
  ['PUERTO RICO', 'PR'],
  ['VIRGIN ISLANDS', 'VI'],
  ['US VIRGIN ISLANDS', 'VI'],
]);

/**
 * The two-letter codes themselves — the canonical stored form. Derived from
 * the map above so the two can never drift.
 */
const US_STATE_CODES = new Set(US_STATE_CODES_BY_NAME.values());

/**
 * Fold a state as typed into its canonical two-letter code.
 *
 * Accepts the code in any case and the full name in any case or spacing, so
 * `CO`, `co`, `Colorado` and `colorado` all resolve to `CO`. Callers that
 * WRITE an address should store this rather than the raw input: the value
 * reaches the geocoder, the notification copy and the buyer's card, and one
 * spelling everywhere is worth more than preserving what was typed.
 * @param state - The state as entered; may be blank.
 * @returns The two-letter code, or null when blank or not a US state.
 */
export function normalizeUsStateCode(
  state: string | null | undefined,
): string | null {
  const value = state?.trim();
  if (!value) return null;

  const upper = value.toUpperCase();
  if (US_STATE_CODES.has(upper)) return upper;

  // "U.S. Virgin Islands" / "New  York" → the map's punctuation-free,
  // single-spaced keys.
  const key = upper.replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();
  return US_STATE_CODES_BY_NAME.get(key) ?? null;
}

/**
 * Rewrite an address's `state` into its canonical code, leaving every other
 * part untouched.
 *
 * A value we cannot place is left exactly as typed — `validatePickupLocationAddress`
 * is what rejects it, and silently discarding it here would hide the problem
 * from the message that explains it.
 * @param address - The address parts as entered; all optional.
 * @returns The same parts with `state` normalized where possible.
 */
export function normalizePickupLocationAddress<
  T extends PickupLocationAddressParts,
>(address: T): T {
  const code = normalizeUsStateCode(address.state);
  return code ? { ...address, state: code } : address;
}

/** Country spellings that mean "United States". Anything else is rejected. */
const US_COUNTRY_NAMES = new Set([
  'US',
  'USA',
  'U.S.',
  'U.S.A.',
  'UNITED STATES',
  'UNITED STATES OF AMERICA',
]);

/** ZIP or ZIP+4. */
const US_POSTCODE_PATTERN = /^\d{5}(-\d{4})?$/;

/**
 * Validate a pickup location's address, returning per-field messages.
 *
 * Nothing here is required — an empty address is legitimate and stays
 * legitimate. This only rejects a value that is *present and wrong*, because a
 * malformed address does not fail loudly: `geocodeAddress` simply returns no
 * coordinates, the location saves, renders fine on orders and in
 * notifications, and is silently absent from the `$near` discovery sweeps that
 * are the commercial point of the feature. A processor gets no signal at all
 * (PRO-689).
 *
 * Keyed by field so the editor can mark the offending input rather than
 * showing one message for the whole block. The resolver is the authority; the
 * client copy is advisory, exactly like the caps above.
 * @param address - The address parts as entered; all optional.
 * @returns Messages keyed by field name; empty when the address is acceptable.
 */
export function validatePickupLocationAddress(
  address: PickupLocationAddressParts | null | undefined,
): Partial<Record<keyof PickupLocationAddressParts, string>> {
  const errors: Partial<Record<keyof PickupLocationAddressParts, string>> = {};
  if (!address) return errors;

  const postcode = address.postcode?.trim();
  if (postcode && !US_POSTCODE_PATTERN.test(postcode)) {
    errors.postcode = 'Enter a 5-digit ZIP code (or ZIP+4).';
  } else if (postcode && /^0+$/.test(postcode.replace(/-/g, ''))) {
    // Passes the digit-count test but is not a real ZIP — the lowest in use is
    // 00501. This is what a placeholder like "00000" looks like.
    errors.postcode = 'Enter a real ZIP code.';
  }

  // Accepts "CO" and "Colorado" alike; only a value that is no US state at all
  // is wrong. See `normalizeUsStateCode` for why rejecting full names was a
  // regression rather than a tightening.
  const state = address.state?.trim();
  if (state && !normalizeUsStateCode(state)) {
    errors.state = 'Enter a US state — a code like MT, or its full name.';
  }

  const country = address.country?.trim();
  if (country && !US_COUNTRY_NAMES.has(country.toUpperCase())) {
    errors.country = 'Pickup locations must be in the United States.';
  }

  // ── Placeability ────────────────────────────────────────────────────────
  // Geocoding is what makes a location discoverable, and only two shapes place
  // a US address reliably: a ZIP, or a city paired with its state. A city on
  // its own is not enough, and that is the hole the first cut left open — the
  // editor hardcodes `country: 'US'`, so the country rule never fires from the
  // UI and `{ address_1: '123 Queen St', city: 'Toronto' }` passed every other
  // check. It saved, rendered fine on orders and in the reminder, geocoded to
  // nothing or to the wrong US city, and vanished from the `$near` sweeps —
  // exactly the silent failure this function exists to catch (PRO-689 review).
  //
  // Skipped when a value is already flagged above: fixing that value is what
  // makes the address placeable, and two messages for one mistake reads as two
  // mistakes.
  const hasStreet = Boolean(address.address_1?.trim());
  const hasCity = Boolean(address.city?.trim());
  if (
    !errors.postcode &&
    !errors.state &&
    !postcode &&
    (hasStreet || hasCity)
  ) {
    if (!hasCity) {
      errors.city =
        'Add a city, or a ZIP, so this address can be found on the map.';
    }
    if (!state) {
      errors.state =
        'Add a state, or a ZIP, so this address can be found on the map.';
    }
  }

  return errors;
}
